import { useState } from "react";
import { useUserPublicProfile } from "../lib/api/useProfile";
import { Sheet } from "./ux/Sheet";
import { Loader } from "./Loader";
import { PublicProfile } from "./PublicProfile";

/**
 * Opens a user's public profile in the shared animated Sheet. Fetches lazily
 * (only while open). Reusable anywhere a user is shown — chat, kikis, etc.
 */
export function PublicProfileModal({
  userId,
  isOpen,
  onClose,
  fresh = false,
}: {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Always refetch on open instead of serving the cached profile (e.g. the
   *  user previewing their own just-edited profile). */
  fresh?: boolean;
}) {
  const { data, isLoading } = useUserPublicProfile(
    isOpen ? userId : null,
    fresh
      ? { staleTime: 0, refetchOnMount: "always" }
      : { staleTime: 60_000 },
  );
  const profile = data?.success ? data.data : null;

  return (
    <Sheet open={isOpen} onClose={onClose} title="Profile">
      {isLoading || !profile ? (
        <div className="py-12 flex justify-center">
          <Loader />
        </div>
      ) : (
        <PublicProfile profile={profile} />
      )}
    </Sheet>
  );
}

/**
 * A button that opens a user's public profile in a modal. Wrap any trigger
 * (avatar, name, row) — it manages its own open state.
 */
export function PublicProfileButton({
  userId,
  className,
  children,
  ariaLabel,
}: {
  userId: string;
  className?: string;
  children: React.ReactNode;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} aria-label={ariaLabel}>
        {children}
      </button>
      <PublicProfileModal userId={userId} isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
}
